import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  policy_no: string;
  title: string;
  category: string;
  summary: string;
  effective_from: string;
  status: string;
};

export default function GovernancePolicyListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.governance.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-governance-policies', status, category, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/governance/policies', {
        params: { status, category, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('governancePolicies')}
      description={t('governancePoliciesIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/governance')}>{t('governance')}</Button>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/governance/policies/new')}>
              {t('newGovernancePolicy')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 120 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'draft', label: t('statusDraft') },
                  { value: 'active', label: t('statusActive') },
                  { value: 'retired', label: t('statusRetired') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('category')}>
              <Select
                allowClear
                style={{ minWidth: 160 }}
                value={category}
                onChange={(v) => setCategory(v || undefined)}
                options={[
                  { value: 'safeguarding_ops', label: t('govCatSafeguarding') },
                  { value: 'attendance_ops', label: t('govCatAttendance') },
                  { value: 'academic_ops', label: t('govCatAcademic') },
                  { value: 'fee_ops', label: t('govCatFee') },
                  { value: 'hr_ops', label: t('govCatHr') },
                  { value: 'facilities', label: t('govCatFacilities') },
                  { value: 'other', label: t('govCatOther') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/governance/policies/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('policyNo'), dataIndex: 'policy_no', width: 150 },
              { title: t('title'), dataIndex: 'title' },
              { title: t('category'), dataIndex: 'category', width: 140 },
              { title: t('effectiveFrom'), dataIndex: 'effective_from', width: 120 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
